import { TAURI_TITLE_BAR_HEIGHT } from '@/components/layout/TauriTitleBar'
import { Button } from '@/components/ui/button'
import { useAutoUpdater } from '@/hooks/useAutoUpdater'
import { createLogger } from '@/lib/logger'
import { isTauri } from '@/lib/utils/tauri'
import { useUpdateStore } from '@/store/update.store'
import {
  ArrowsClockwise,
  Download,
  RocketLaunch,
  X,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

const logger = createLogger('components/shared/UpdateBanner')

/**
 * Update Banner Component
 * Shows a persistent banner at the top of the app when an update is available
 * Only visible in desktop (Tauri) environment
 */

export const UPDATE_BANNER_HEIGHT = 40

export function useUpdateBannerVisible() {
  const updateAvailable = useUpdateStore(state => state.updateAvailable)
  const dismissed = useUpdateStore(state => state.dismissed)

  return isTauri() && updateAvailable && !dismissed
}

export function UpdateBanner() {
  const navigate = useNavigate()
  const {
    updateInfo,
    installing,
    updateDownloaded,
    downloadProgress: predownloadProgress,
    dismissUpdate,
  } = useUpdateStore()
  const { installUpdate } = useAutoUpdater({ enableListeners: false })

  const isVisible = useUpdateBannerVisible()
  const downloadProgress = updateDownloaded ? 100 : predownloadProgress
  if (!isVisible) {
    return null
  }

  const handleInstall = async () => {
    try {
      if (!isTauri()) {
        return
      }

      await installUpdate()
    } catch (error) {
      logger.error('Update installation failed:', error)
    }
  }

  return (
    <div className="relative flex h-10 w-full items-center justify-center gap-3 bg-blue-600 px-12 text-white shadow-sm">
      <div className="flex min-w-0 items-center gap-2">
        <Download className="h-4 w-4 shrink-0" weight="bold" />
        <span className="truncate text-sm font-medium">
          A new version ({updateInfo?.version}) is available!
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/whats-new')}
          className="h-7 gap-1.5 rounded-full border border-white/35 bg-white/10 px-3 text-xs font-medium text-white hover:bg-white/20 hover:text-white"
        >
          <RocketLaunch className="h-3 w-3" weight="bold" />
          What&apos;s new
        </Button>

        <Button
          size="sm"
          variant="default"
          onClick={handleInstall}
          disabled={installing}
          className="h-7 gap-1.5 rounded-full bg-white px-3 text-xs font-semibold text-blue-700 hover:bg-blue-50 hover:text-blue-800"
        >
          {installing ? (
            <>
              <ArrowsClockwise className="h-3 w-3 animate-spin" />
              {downloadProgress > 0 ? 'Installing…' : 'Downloading…'}
            </>
          ) : (
            <>
              <Download className="h-3 w-3" weight="bold" />
              Update now
            </>
          )}
        </Button>
      </div>

      <button
        onClick={dismissUpdate}
        className="absolute right-4 rounded p-1 transition-colors hover:bg-primary-foreground/10"
        title="Dismiss (update will still be available)"
        aria-label="Dismiss update notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function TauriUpdateBannerSlot() {
  const isVisible = useUpdateBannerVisible()

  if (!isVisible) {
    return null
  }

  return (
    <div
      className="fixed left-0 right-0 z-[49]"
      style={{ top: TAURI_TITLE_BAR_HEIGHT }}
    >
      <UpdateBanner />
    </div>
  )
}
